import {
  getOrganicProductCount,
  findSpotLightPlacement,
} from "../../common/CriteoAdvertsForRecs";
import { APP, PLACEMENT } from "@asosteam/asos-web-adverts-sdk";
import { logError, ERROR_TYPE } from "../utils/newRelicLogger";

export const normalizeCriteoResponse = (response = {}) => {
  if (Array.isArray(response?.products)) {
    return response;
  }

  const placements = Array.isArray(response?.placements)
    ? response.placements
    : [];

  const banner = findSpotLightPlacement(placements);
  const bannerProp = banner ? { banner } : {};
  const responseId = response.responseId || response["page-uid"];

  for (const placementGroup of placements) {
    if (!placementGroup || typeof placementGroup !== "object") {
      continue;
    }

    for (const placementValue of Object.values(placementGroup)) {
      if (!Array.isArray(placementValue)) {
        continue;
      }

      const sponsoredPlacement = placementValue.find((item) =>
        Array.isArray(item?.products),
      );

      if (sponsoredPlacement) {
        return {
          products: sponsoredPlacement.products,
          OnLoadBeacon: sponsoredPlacement.OnLoadBeacon,
          OnViewBeacon: sponsoredPlacement.OnViewBeacon,
          responseId,
          ...bannerProp,
        };
      }
    }
  }

  return { ...response, ...bannerProp };
};

const ensureMockLineItemIds = (response = {}) => {
  if (!Array.isArray(response?.products)) {
    return response;
  }

  return {
    ...response,
    products: response.products.map((product, index) => {
      if (product?.LineItemID) {
        return product;
      }

      return {
        ...product,
        LineItemID: `mock-line-item-${product?.ProductId || index}`,
      };
    }),
  };
};

const criteoApi = async (criteoRequiredParams) => {
  const {
    productIdCollection,
    productId,
    isInStock,
    price,
    advertsSdkInstance,
    adsConfig,
    isCriteoBannerBucket = false,
  } = criteoRequiredParams;

  const PAGE_NUMBER = 1;

  const hasSponsoredProductCandidates = Boolean(productIdCollection?.length);

  if (
    (!hasSponsoredProductCandidates && !isCriteoBannerBucket) ||
    !advertsSdkInstance?.getAdverts
  ) {
    return {};
  }

  const {
    current: { value },
  } = price;

  const listSize = hasSponsoredProductCandidates
    ? getOrganicProductCount(productIdCollection, adsConfig)
    : 0;
  const productIdCollectionPiped = hasSponsoredProductCandidates
    ? productIdCollection.join("|")
    : "";

  const parameters = {
    "page-number": PAGE_NUMBER,
    "list-size": listSize,
    "parent-item": productId,
    "item-whitelist": productIdCollectionPiped,
    "list-price": value || 0,
    filters: "",
    availability: Number(isInStock),
    price: value || 0,
  };

  const options = {
    app: APP.PDP,
    placement: PLACEMENT.SPONSORED_PRODUCTS,
    // Request the Criteo on-site display (BDSS) banner from this single
    // sponsored-products call ONLY in the Criteo banner bucket (v2), where we
    // actually paint it (via CriteoSpotLightAdvert, fed from this response).
    // In the Google-ad (v1) and no-ad (v3) buckets we never paint a banner, so
    // we must not request one: the adverts SDK derives the wire `nocall` value
    // from these flags, and requestDisplayAdverts=false makes it send
    // nocall=pd so Criteo returns sponsored products only. Requesting a banner
    // we never paint counts against the Criteo painted rate.
    requestDisplayAdverts: isCriteoBannerBucket,
    requestSponsoredProducts: true,
    breakCache: true,
  };

  const mockOverride =
    window?.asos?.pdp?.config?.mockSponsoredYmal ||
    (() => {
      try {
        const stored = sessionStorage.getItem("mockSponsoredYmal");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })();

  if (mockOverride) {
    return ensureMockLineItemIds(normalizeCriteoResponse(mockOverride));
  }

  try {
    const response = await advertsSdkInstance.getAdverts({
      options,
      parameters,
    });

    return normalizeCriteoResponse(response);
  } catch (error) {
    logError(ERROR_TYPE.NonCriticalAppError, {
      message: error.message,
      action: "CriteoAdvertCall",
    });
    return {};
  }
};

export default criteoApi;
